import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Divider,
  Grid,
  CircularProgress,
  alpha,
  useTheme,
  TextField,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { getBloodPressureByClient, updateBloodPressure } from "../../../../../../api/services/observations";
import { enqueueSnackbar } from "notistack";
import { QK } from "../../../../../../base/qk";
import { useParams } from "react-router-dom";
import CustomCheckbox from "../../../../../CustomForm/CustomCheckbox";

const EditBloodPressureModal = ({ show, handleClose, selectedID }) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { id: ClientID } = useParams();

  const [formData, setFormData] = useState({
    systolic: "",
    diastolic: "",
    situation: "None recorded",
    notes: "",
    date_time: "",
    handover: false,
    manager: false,
  });

  const { data: bpListData, isLoading, refetch } = useQuery(
    [QK.blood_pressure, ClientID],
    () => getBloodPressureByClient(ClientID),
    {
      enabled: !!ClientID && show,
    }
  );

  useEffect(() => {
    if (show && bpListData && Array.isArray(bpListData)) {
      const selectedRecord = bpListData.find((item) => item.id === selectedID);
      if (selectedRecord) {
        setFormData({
          systolic: selectedRecord.systolic || "",
          diastolic: selectedRecord.diastolic || "",
          situation: selectedRecord.situation || "None recorded",
          notes: selectedRecord.notes || "",
          date_time: selectedRecord.date_time
            ? new Date(selectedRecord.date_time).toISOString().slice(0, 16)
            : "",
          handover: selectedRecord.handover || false,
          manager: selectedRecord.manager || false,
        });
      }
    }
  }, [bpListData, selectedID, show]);

  const updateMutation = useMutation(
    (newData) => updateBloodPressure(selectedID, newData),
    {
      onSuccess: () => {
        enqueueSnackbar("Blood pressure entry updated successfully", {
          variant: "success",
        });
        queryClient.invalidateQueries([QK.blood_pressure, ClientID]);
        handleClose();
      },
      onError: (error) => {
        enqueueSnackbar(error?.response?.data?.message || "Failed to update blood pressure entry", {
          variant: "error",
        });
      },
    }
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckBox = (checked, name) => {
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      systolic: parseInt(formData.systolic),
      diastolic: parseInt(formData.diastolic),
      date_time: new Date(formData.date_time).toISOString(),
    };
    updateMutation.mutate(payload);
  };

  const handleDialogClose = () => {
    setFormData({
      systolic: "",
      diastolic: "",
      situation: "None recorded",
      notes: "",
      date_time: "",
      handover: false,
      manager: false,
    });
    handleClose();
  };

  const situationOptions = [
    "None recorded",
    "Sitting",
    "Lying",
    "Standing",
    "After exercise",
  ];

  if (isLoading) {
    return (
      <Dialog open={show} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogContent sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={show}
      onClose={handleDialogClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow: "0px 24px 48px -12px rgba(16, 24, 40, 0.25)",
        },
      }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2.5,
            pb: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "10px",
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <EditIcon sx={{ color: theme.palette.primary.main, fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={600} sx={{ color: "#1A1A2E" }}>
                Edit Blood Pressure Entry
              </Typography>
              <Typography variant="caption" sx={{ color: "#6B7280" }}>
                Update blood pressure record details
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleDialogClose}
            size="small"
            sx={{
              color: "#9CA3AF",
              "&:hover": { bgcolor: "#F3F4F6", color: "#4B5563" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ opacity: 0.4 }} />
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ p: 2.5, pt: 2 }}>
          <Grid container spacing={2.5}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Systolic *"
                name="systolic"
                type="number"
                value={formData.systolic}
                onChange={handleChange}
                required
                fullWidth
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Diastolic *"
                name="diastolic"
                type="number"
                value={formData.diastolic}
                onChange={handleChange}
                required
                fullWidth
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                label="Situation *"
                name="situation"
                value={formData.situation}
                onChange={handleChange}
                fullWidth
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                  },
                }}
              >
                {situationOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Date & Time *"
                name="date_time"
                type="datetime-local"
                value={formData.date_time}
                onChange={handleChange}
                required
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                multiline
                rows={2}
                fullWidth
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                <CustomCheckbox
                  label="Share with Manager"
                  name="manager"
                  checked={formData.manager}
                  onClick={(checked) => handleCheckBox(checked, "manager")}
                />
                <CustomCheckbox
                  label="Handover"
                  name="handover"
                  checked={formData.handover}
                  onClick={(checked) => handleCheckBox(checked, "handover")}
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 2.5, pt: 0, gap: 1.5 }}>
          <Button
            onClick={handleDialogClose}
            variant="outlined"
            disabled={updateMutation.isLoading}
            sx={{
              textTransform: "none",
              fontWeight: 500,
              borderRadius: "10px",
              borderColor: "#D0D5DD",
              color: "#344054",
              "&:hover": {
                bgcolor: "#F9FAFB",
                borderColor: "#98A2B3",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={updateMutation.isLoading}
            startIcon={updateMutation.isLoading ? <CircularProgress size={20} /> : <SaveIcon />}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "10px",
              bgcolor: "#2469F0",
              px: 3,
              "&:hover": {
                bgcolor: "#1A5BD4",
                boxShadow: "0px 4px 12px rgba(36, 105, 240, 0.3)",
              },
            }}
          >
            {updateMutation.isLoading ? "Saving..." : "Update Blood Pressure"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditBloodPressureModal;